import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Instagram, Twitter } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import logoUrl from '../assets/logo.png';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
      toast({
        title: 'Subscribed!',
        description: response.data.message || 'Welcome aboard — we\'ll keep you updated under the stars.',
      });
      setEmail('');
    } catch (error) {
      toast({
        title: 'Subscription failed',
        description: error.response?.data?.detail || 'Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative bg-[#050814] text-[#F4F1EA] border-t border-[#B8860B]/20 overflow-hidden">
      {/* Subtle constellation pattern */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 12% 20%, #E5C158 1px, transparent 2px), radial-gradient(circle at 60% 40%, #E6E8FA 1px, transparent 2px), radial-gradient(circle at 80% 75%, #D4AF37 1px, transparent 2px), radial-gradient(circle at 25% 80%, #ffffff 1px, transparent 2px)',
          backgroundSize: '300px 200px',
        }}
      />

      {/* Newsletter strip */}
      <div className="relative bg-gradient-to-b from-[#0B132B] to-[#050814] border-b border-[#B8860B]/15 py-14">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <p className="uppercase tracking-[0.32em] text-[10px] text-[#D4AF37] mb-3">
              The Ranch Dispatch
            </p>
            <h3 className="font-display text-3xl md:text-4xl font-semibold mb-3 text-[#F4F1EA]">
              Stay close to the stars
            </h3>
            <p className="text-[#F4F1EA]/70 mb-6 text-sm md:text-base">
              Subscribe for openings, full-moon events, and seasonal stargazing nights.
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              data-testid="newsletter-form"
            >
              <Input
                data-testid="newsletter-email-input"
                type="email"
                placeholder="you@cosmos.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#050814] border-[#B8860B]/40 text-[#F4F1EA] placeholder:text-[#F4F1EA]/40 focus-visible:ring-[#B8860B]"
              />
              <Button
                data-testid="newsletter-subscribe-btn"
                type="submit"
                disabled={loading}
                className="bg-[#B8860B] hover:bg-[#D4AF37] text-[#050814] font-semibold whitespace-nowrap rounded-full px-6"
              >
                {loading ? 'Subscribing…' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="relative container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img src={logoUrl} alt="Creek Bottom Ranch" className="h-20 w-auto mb-4 drop-shadow-[0_0_20px_rgba(212,175,55,0.18)]" />
            <p className="text-[#F4F1EA]/75 text-sm leading-relaxed mb-5 italic">
              "Take a trip to the stars."
            </p>
            <p className="text-[#F4F1EA]/65 text-sm leading-relaxed">
              An intimate stargazing ranch in East Texas. RV sites, primitive camping, and a deck under the deepest skies.
            </p>
            <div className="flex space-x-3 mt-5">
              {[Facebook, Instagram, Twitter].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label="Social"
                  className="w-9 h-9 rounded-full bg-[#0B132B] border border-[#B8860B]/30 flex items-center justify-center hover:bg-[#B8860B] hover:border-[#B8860B] group transition-colors"
                >
                  <Icon size={17} className="text-[#D4AF37] group-hover:text-[#050814] transition-colors" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display text-xl font-semibold mb-4 text-[#D4AF37]">Explore</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-[#F4F1EA]/75 hover:text-[#E5C158] transition-colors">Home</Link></li>
              <li><Link to="/booking" className="text-[#F4F1EA]/75 hover:text-[#E5C158] transition-colors">Book Your Stay</Link></li>
              <li><Link to="/equipment" className="text-[#F4F1EA]/75 hover:text-[#E5C158] transition-colors">Equipment Rentals</Link></li>
              <li><Link to="/gallery" className="text-[#F4F1EA]/75 hover:text-[#E5C158] transition-colors">Gallery</Link></li>
              <li><Link to="/directions" className="text-[#F4F1EA]/75 hover:text-[#E5C158] transition-colors">Directions</Link></li>
              <li><Link to="/contact" className="text-[#F4F1EA]/75 hover:text-[#E5C158] transition-colors">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-xl font-semibold mb-4 text-[#D4AF37]">Find Us</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-[#F4F1EA]/75">
                  2724 Private Road 8160<br />Lovelady, TX 75851
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a href="tel:8327140101" className="text-[#F4F1EA]/75 hover:text-[#E5C158] transition-colors">
                  (832) 714-0101
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#D4AF37] flex-shrink-0" />
                <a href="mailto:info@creekbottomranch.com" className="text-[#F4F1EA]/75 hover:text-[#E5C158] transition-colors">
                  info@creekbottomranch.com
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-xl font-semibold mb-4 text-[#D4AF37]">Office Hours</h4>
            <ul className="space-y-2 text-sm text-[#F4F1EA]/75">
              <li className="flex justify-between"><span>Mon — Fri</span><span className="font-medium">8am – 8pm</span></li>
              <li className="flex justify-between"><span>Saturday</span><span className="font-medium">9am – 6pm</span></li>
              <li className="flex justify-between"><span>Sunday</span><span className="font-medium">10am – 5pm</span></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-[#B8860B]/15 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-[#F4F1EA]/55">
          <p>&copy; {new Date().getFullYear()} Creek Bottom Ranch. Est. 2025 — under the stars.</p>
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <li>
              <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#E5C158] transition-colors">
                Privacy Policy
              </Link>
            </li>
            <li className="text-[#F4F1EA]/30">·</li>
            <li>
              <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#E5C158] transition-colors">
                Terms &amp; Conditions
              </Link>
            </li>
            <li className="text-[#F4F1EA]/30">·</li>
            <li>
              <a href="sms:&body=STOP" className="hover:text-[#E5C158] transition-colors" title="Reply STOP to any text from us to unsubscribe">
                SMS Opt-out: Reply STOP
              </a>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
